import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../../../../store/slices/user';
import {
  useUpdateSubscriptionBalanceMutation,
  useGetSubscriptionSummariesByUserIdQuery,
} from '../../../../../store/services/api';
import { DistributionEntry } from '../../../types';

type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

type SubmissionProgress = {
  current: number;
  total: number;
  currentSub: string;
  phase: 'asset' | 'stable';
};

export function useSubmitDistribution() {
  const userId = useSelector(selectUserId);
  const [updateBalance] = useUpdateSubscriptionBalanceMutation();
  const { refetch } = useGetSubscriptionSummariesByUserIdQuery(userId, {
    refetchOnMountOrArgChange: false,
  });

  const [state, setState] = useState<SubmissionState>('idle');
  const [progress, setProgress] = useState<SubmissionProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedSub, setFailedSub] = useState<string | null>(null);

  const submit = useCallback(
    async (changedEntries: DistributionEntry[]) => {
      setState('submitting');
      setError(null);
      setFailedSub(null);

      const assetChanges = changedEntries.filter(
        (e) => Math.abs(e.currentAssetBalance - e.originalAssetBalance) > 0.001,
      );
      const stableOnlyChanges = changedEntries.filter(
        (e) =>
          Math.abs(e.currentAssetBalance - e.originalAssetBalance) <= 0.001 &&
          Math.abs(e.currentStableBalance - e.originalStableBalance) > 0.01,
      );

      const totalSteps = assetChanges.length + stableOnlyChanges.length;
      let step = 0;

      // Phase 1: Asset changes (sequential — each triggers a real exchange order)
      for (const entry of assetChanges) {
        step++;
        setProgress({
          current: step,
          total: totalSteps,
          currentSub: entry.botFriendlyName,
          phase: 'asset',
        });

        try {
          await updateBalance({
            userId,
            subscriptionId: entry.subscriptionId,
            assetBalance: entry.currentAssetBalance,
            shouldOrder: true,
          }).unwrap();
        } catch (err) {
          setState('error');
          setError(`Asset order failed for ${entry.botFriendlyName}`);
          setFailedSub(entry.botFriendlyName);
          return;
        }
      }

      // Refetch to get actual post-order balances
      if (assetChanges.length > 0) {
        await refetch();
      }

      // Phase 2: Stable balance changes
      const allStableChanges = [
        ...assetChanges.filter(
          (e) =>
            Math.abs(e.currentStableBalance - e.originalStableBalance) > 0.01,
        ),
        ...stableOnlyChanges,
      ];

      for (const entry of allStableChanges) {
        step++;
        setProgress({
          current: step,
          total: totalSteps,
          currentSub: entry.botFriendlyName,
          phase: 'stable',
        });

        try {
          await updateBalance({
            userId,
            subscriptionId: entry.subscriptionId,
            stableBalance: entry.currentStableBalance,
            shouldOrder: false,
          }).unwrap();
        } catch (err) {
          setState('error');
          setError(`Stable balance update failed for ${entry.botFriendlyName}`);
          setFailedSub(entry.botFriendlyName);
          return;
        }
      }

      // Final refetch
      await refetch();
      setState('success');
      setProgress(null);
    },
    [updateBalance, userId, refetch],
  );

  const resetState = useCallback(() => {
    setState('idle');
    setProgress(null);
    setError(null);
    setFailedSub(null);
  }, []);

  return {
    submit,
    state,
    progress,
    error,
    failedSub,
    resetState,
  };
}
